import { Alert, App as AntApp, Button, Card, DatePicker, Form, Grid, Select, Space, Switch, Table, Tag, TimePicker, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { classKeys, defaultYear, formatDate, useAcademicYears, useActiveTeachers } from "../app/classes.ts";
import { formatSchoolDateTime } from "../app/time.ts";
import {
  formatTime,
  unmarkedKeys,
  useReminderSettings,
  useUnmarkedLessons,
  type ReminderSettingsDetails,
  type UnmarkedLessonItem,
} from "../app/unmarkedLessons.ts";

const PAGE_SIZE = 25;
const DISPLAY_FORMAT = "DD.MM.YYYY";
const ISO_DATE = "YYYY-MM-DD";

/**
 * Lessons not marked by the deadline (spec story 41, bd-3qg.8): the flagged lessons by date, teacher and class, and the teacher
 * reminder times. Requires attendance.readAll; only attendance.manage may change the times.
 */
export function UnmarkedLessonsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("unmarkedLessons.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("unmarkedLessons.subtitle")}</Typography.Text>
      </div>
      <FlaggedLessonsCard canFilterTeachers={hasPermission("people.read")} canFilterClasses={hasPermission("academic.read")} />
      <ReminderSettingsCard canManage={hasPermission("attendance.manage")} />
    </Space>
  );
}

function FlaggedLessonsCard({ canFilterTeachers, canFilterClasses }: { canFilterTeachers: boolean; canFilterClasses: boolean }) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [dates, setDates] = useState<[Dayjs, Dayjs] | null>(null);
  const [teacherId, setTeacherId] = useState<string>();
  const [classId, setClassId] = useState<string>();
  const [includeCleared, setIncludeCleared] = useState(false);
  const [page, setPage] = useState(1);

  const teachers = useActiveTeachers(canFilterTeachers);
  const years = useAcademicYears();
  const year = defaultYear(years.data);
  const classes = useQuery({
    queryKey: classKeys.list(year?.id),
    enabled: canFilterClasses && year !== undefined,
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes", { params: { query: year ? { AcademicYearId: year.id } : {} } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const lessons = useUnmarkedLessons({
    from: dates?.[0].format(ISO_DATE),
    to: dates?.[1].format(ISO_DATE),
    teacherId,
    classId,
    includeCleared,
    page,
    pageSize: PAGE_SIZE,
  });
  const resetPage = () => setPage(1);

  const columns: TableColumnsType<UnmarkedLessonItem> = [
    { title: t("unmarkedLessons.columns.date"), key: "date", render: (_: unknown, row: UnmarkedLessonItem) => formatDate(row.date) },
    {
      title: t("unmarkedLessons.columns.lesson"),
      key: "lesson",
      render: (_: unknown, row: UnmarkedLessonItem) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{row.subjectName}</Typography.Text>
          <Typography.Text type="secondary">
            {t("unmarkedLessons.period", { period: Number(row.period), start: formatTime(row.startTime), end: formatTime(row.endTime) })}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: t("unmarkedLessons.columns.class"),
      key: "class",
      render: (_: unknown, row: UnmarkedLessonItem) => <Link to={`/academic/classes/${row.classId}`}>{row.className}</Link>,
    },
    {
      title: t("unmarkedLessons.columns.teacher"),
      key: "teacher",
      render: (_: unknown, row: UnmarkedLessonItem) => row.teacherName ?? <Typography.Text type="secondary">{t("unmarkedLessons.noTeacher")}</Typography.Text>,
    },
    {
      title: t("unmarkedLessons.columns.marked"),
      key: "marked",
      align: "right",
      render: (_: unknown, row: UnmarkedLessonItem) => t("unmarkedLessons.markedOf", { marked: Number(row.markedCount), roster: Number(row.rosterCount) }),
    },
    {
      title: t("unmarkedLessons.columns.status"),
      key: "status",
      render: (_: unknown, row: UnmarkedLessonItem) =>
        row.clearedAt ? (
          <Space orientation="vertical" size={0}>
            <Tag color="green">{t("unmarkedLessons.cleared")}</Tag>
            <Typography.Text type="secondary">{formatSchoolDateTime(row.clearedAt)}</Typography.Text>
          </Space>
        ) : (
          <Tag color="red">{t("unmarkedLessons.open")}</Tag>
        ),
    },
  ];

  return (
    <Card title={t("unmarkedLessons.listTitle")}>
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Typography.Text type="secondary">{t("unmarkedLessons.listHint")}</Typography.Text>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <DatePicker.RangePicker
            value={dates}
            format={DISPLAY_FORMAT}
            aria-label={t("unmarkedLessons.filters.range")}
            placeholder={[t("unmarkedLessons.filters.from"), t("unmarkedLessons.filters.to")]}
            style={{ width: screens.md ? 260 : "100%" }}
            onChange={(value) => {
              setDates(value?.[0] && value[1] ? [value[0], value[1]] : null);
              resetPage();
            }}
          />
          {canFilterTeachers && (
            <Select
              allowClear
              showSearch={{ optionFilterProp: "label" }}
              value={teacherId}
              placeholder={t("unmarkedLessons.filters.teacher")}
              aria-label={t("unmarkedLessons.filters.teacher")}
              loading={teachers.isPending}
              options={(teachers.data ?? []).map((teacher) => ({ value: teacher.id, label: `${teacher.lastName} ${teacher.firstName}` }))}
              style={{ width: screens.md ? 220 : "100%" }}
              onChange={(value?: string) => {
                setTeacherId(value);
                resetPage();
              }}
            />
          )}
          {canFilterClasses && (
            <Select
              allowClear
              showSearch={{ optionFilterProp: "label" }}
              value={classId}
              placeholder={t("unmarkedLessons.filters.class")}
              aria-label={t("unmarkedLessons.filters.class")}
              loading={classes.isPending && classes.fetchStatus !== "idle"}
              options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              style={{ width: screens.md ? 160 : "100%" }}
              onChange={(value?: string) => {
                setClassId(value);
                resetPage();
              }}
            />
          )}
          <Space size={8}>
            <Switch
              checked={includeCleared}
              aria-label={t("unmarkedLessons.filters.includeCleared")}
              onChange={(checked) => {
                setIncludeCleared(checked);
                resetPage();
              }}
            />
            <Typography.Text>{t("unmarkedLessons.filters.includeCleared")}</Typography.Text>
          </Space>
        </div>
        {lessons.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void lessons.refetch()}>{t("app.retry")}</Button>} />}
        <Table<UnmarkedLessonItem>
          rowKey="lessonId"
          size={screens.md ? "middle" : "small"}
          loading={lessons.isPending || (lessons.isFetching && lessons.isPlaceholderData)}
          columns={columns}
          dataSource={lessons.data?.items ?? []}
          locale={{ emptyText: t("unmarkedLessons.empty") }}
          scroll={{ x: "max-content" }}
          pagination={{ current: page, pageSize: PAGE_SIZE, total: Number(lessons.data?.totalCount ?? 0), onChange: setPage, hideOnSinglePage: true, showSizeChanger: false }}
        />
      </Space>
    </Card>
  );
}

interface SettingsForm {
  unmarkedReminderTime?: Dayjs;
  homeworkReminderTime?: Dayjs;
}

function toPicker(value: string): Dayjs {
  const [h, m] = value.split(":");
  return dayjs().hour(Number(h)).minute(Number(m)).second(0);
}

function ReminderSettingsCard({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SettingsForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const settings = useReminderSettings();

  useEffect(() => {
    if (!settings.data) return;
    form.setFieldsValue({ unmarkedReminderTime: toPicker(settings.data.unmarkedReminderTime), homeworkReminderTime: toPicker(settings.data.homeworkReminderTime) });
  }, [settings.data, form]);

  const mutation = useMutation({
    mutationFn: async (values: SettingsForm) => {
      const { data, error, response } = await api.PUT("/attendance/reminder-settings", {
        body: {
          unmarkedReminderTime: values.unmarkedReminderTime ? values.unmarkedReminderTime.format("HH:mm:00") : null,
          homeworkReminderTime: values.homeworkReminderTime ? values.homeworkReminderTime.format("HH:mm:00") : null,
        },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async () => {
      setSummary([]);
      await queryClient.invalidateQueries({ queryKey: unmarkedKeys.settings });
      void message.success(t("unmarkedLessons.settings.saved"));
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const data: ReminderSettingsDetails | undefined = settings.data;
  return (
    <Card title={t("unmarkedLessons.settings.title")}>
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Typography.Text type="secondary">{t("unmarkedLessons.settings.hint", { deadline: formatTime(data?.deadlineTime) })}</Typography.Text>
        {settings.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void settings.refetch()}>{t("app.retry")}</Button>} />}
        {data && (
          <Typography.Text type="secondary">
            {data.isDefault || !data.updatedAt
              ? t("unmarkedLessons.settings.defaults")
              : t("unmarkedLessons.settings.updatedBy", { name: data.updatedByName ?? "", at: formatSchoolDateTime(data.updatedAt) })}
          </Typography.Text>
        )}
        {summary.length > 0 && <Alert type="error" showIcon title={summary.join(" ")} />}
        <Form form={form} layout="vertical" disabled={!canManage || settings.isPending} onFinish={(values) => mutation.mutate(values)}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            <Form.Item
              name="unmarkedReminderTime"
              label={t("unmarkedLessons.settings.unmarked")}
              extra={t("unmarkedLessons.settings.unmarkedHint")}
              rules={[{ required: true, message: t("common.required") }]}
              style={{ minWidth: 220, flex: "1 1 220px" }}
            >
              <TimePicker format="HH:mm" minuteStep={5} allowClear={false} needConfirm={false} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="homeworkReminderTime"
              label={t("unmarkedLessons.settings.homework")}
              extra={t("unmarkedLessons.settings.homeworkHint")}
              rules={[{ required: true, message: t("common.required") }]}
              style={{ minWidth: 220, flex: "1 1 220px" }}
            >
              <TimePicker format="HH:mm" minuteStep={5} allowClear={false} needConfirm={false} style={{ width: "100%" }} />
            </Form.Item>
          </div>
          {canManage && (
            <Button type="primary" htmlType="submit" loading={mutation.isPending}>
              {t("common.save")}
            </Button>
          )}
        </Form>
      </Space>
    </Card>
  );
}
